import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { determineEditorGenerationByProfileAndVersion } from "./manage-workflow-dialog/ManageWorkflowDialog";
import RestoreVersionDialog from "./RestoreVersionDialog";
import style from "./side-panel-version-history.scss";
import { isEditorV3Supported, supportsEditorBasedRestore } from "./WorkflowUtil";
import { History } from "components/icons/History";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import {
    ConnectedWorkflows,
    WorkflowEntityType,
    WorkflowTableData,
    WorkflowVersionHistoryData,
} from "domain/workflows";
import { TemplateTableData, WorkflowTemplateVersionHistoryData } from "domain/workflowTemplates";
import { Action, Category, Label, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { templateService } from "services/workflows/TemplateService";
import { EditorGeneration, WorkflowEditorV3SupportedEntry, workflowService } from "services/workflows/WorkflowService";
import { formatTimestamp } from "utils/format";

import testIds from "testIds.json";

export type RestoreFunction = (
    uuid: string,
    editorVersion: string,
    profile: string,
    restoreVersionId: string,
    editorGeneration?: EditorGeneration,
    connectedWorkflows?: ConnectedWorkflows
) => void;

interface CurrentVersionInfo {
    version: string;
    modifiedDate: string;
}

interface SidePanelVersionHistoryProps {
    currentVersion: CurrentVersionInfo;
    workflowUuid: string;
    entityType: WorkflowEntityType;
    onRestoreVersion?: RestoreFunction;
    onOpenWithV3?: (workflowUuid: string, versionId: string) => void;
    workflowEditorV3Supported?: WorkflowEditorV3SupportedEntry[];
    selectedRow?: WorkflowTableData | TemplateTableData;
    onRefreshTable?: () => void;
    onCloseSidePanel?: () => void;
}

type VersionHistoryData = WorkflowVersionHistoryData | WorkflowTemplateVersionHistoryData;

export function SidePanelVersionHistory(props: SidePanelVersionHistoryProps): JSX.Element {
    const { t } = useTranslation();
    const [versionHistory, setVersionHistory] = useState<VersionHistoryData[]>([]);
    const [loading, setLoading] = useState(true);
    const [showRestoreDialog, setShowRestoreDialog] = useState(false);
    const [selectedVersionForRestore, setSelectedVersionForRestore] = useState<VersionHistoryData | null>(null);
    const [restoring, setRestoring] = useState(false);
    const [restoreSuccess, setRestoreSuccess] = useState(false);
    const [restoreError, setRestoreError] = useState<string | null>(null);

    useEffect(() => {
        const abortController = new AbortController();

        usageStatisticsService.sendEvent({
            label: Label.WORKFLOW_SIDEPANEL,
            action: Action.VIEW_VERSION_HISTORY,
            category: Category.WORKFLOW,
        });

        const fetchVersions =
            props.entityType === WorkflowEntityType.WORKFLOW
                ? workflowService.fetchWorkflowVersionHistory(props.workflowUuid, abortController)
                : templateService.fetchWorkflowTemplateVersionHistory(props.workflowUuid, abortController);

        fetchVersions
            .then((data) => {
                setVersionHistory(data);
                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
            });

        return () => abortController.abort();
    }, [props.workflowUuid, props.entityType]);

    if (loading) {
        return (
            <div>
                <LoadingIndicator />
            </div>
        );
    }

    const handleRestoreClick = (version: VersionHistoryData) => {
        setSelectedVersionForRestore(version);
        setShowRestoreDialog(true);
    };

    const sendRestoreEvent = () => {
        usageStatisticsService.sendEvent({
            label: Label.WORKFLOW_SIDEPANEL,
            action: Action.RESTORE_WORKFLOW_VERSION,
            category: Category.WORKFLOW,
        });
    };

    const handleRestoreConfirm = () => {
        if (selectedVersionForRestore && props.selectedRow) {
            const profile = props.selectedRow.profile;
            const editorVersion = selectedVersionForRestore.editorVersion;
            const versionId = selectedVersionForRestore.s3VersionId;

            if (
                isEditorV3Supported(props.workflowEditorV3Supported ?? [], profile, editorVersion) &&
                props.onOpenWithV3
            ) {
                props.onOpenWithV3(props.workflowUuid, versionId);
                setShowRestoreDialog(false);
                setSelectedVersionForRestore(null);
                sendRestoreEvent();
                return;
            }

            const canRestoreInEditor = supportsEditorBasedRestore(profile, editorVersion);

            if (canRestoreInEditor && props.onRestoreVersion) {
                const connectedWorkflows = props.selectedRow.connectedWorkflows;
                const editorGeneration = determineEditorGenerationByProfileAndVersion(profile, editorVersion, false);

                props.onRestoreVersion(
                    props.workflowUuid,
                    editorVersion,
                    profile,
                    versionId,
                    editorGeneration,
                    connectedWorkflows
                );

                setShowRestoreDialog(false);
                setSelectedVersionForRestore(null);
            } else {
                setRestoring(true);
                const restorePromise =
                    props.entityType === WorkflowEntityType.WORKFLOW
                        ? workflowService.restoreWorkflow(props.workflowUuid, versionId)
                        : templateService.restoreTemplate(props.workflowUuid, versionId);

                restorePromise
                    .then(() => {
                        setRestoring(false);
                        setRestoreSuccess(true);
                        setRestoreError(null);
                    })
                    .catch(() => {
                        console.error("Restore failed");
                        setRestoring(false);
                        setRestoreError("Restore failed");
                    });
            }

            sendRestoreEvent();
        }
    };

    const handleRestoreDialogClose = () => {
        if (restoreSuccess) {
            if (props.onRefreshTable) {
                props.onRefreshTable();
            }
            if (props.onCloseSidePanel) {
                props.onCloseSidePanel();
            }
        }

        setShowRestoreDialog(false);
        setSelectedVersionForRestore(null);
        setRestoreSuccess(false);
        setRestoreError(null);
    };

    return (
        <div className={style.versionHistoryContainer}>
            <div className={style.versionHistoryItem}>
                <div className={style.versionInfo}>
                    <div className={style.versionDate}>{formatTimestamp(props.currentVersion.modifiedDate)}</div>
                    <div className={style.versionNumber}>{props.currentVersion.version}</div>
                </div>
                <div className={style.versionAction}>
                    <span className={style.currentBadge}>
                        {t("WorkflowsTable.sidePanel.tabs.versionHistory.current")}
                    </span>
                </div>
            </div>

            {versionHistory.map((item, index) => (
                <div key={index} className={style.versionHistoryItem}>
                    <div className={style.versionInfo}>
                        <div className={style.versionDate}>{formatTimestamp(item.createdDate)}</div>
                        <div className={style.versionNumber}>{item.editorVersion}</div>
                    </div>
                    <div className={style.versionAction}>
                        <div
                            className={style.restoreButton}
                            onClick={() => handleRestoreClick(item)}
                            data-testid={testIds.workArea.workflows.sidePanel.tabs.versionHistory.restoreButton}
                        >
                            <History />
                            {t("WorkflowsTable.sidePanel.tabs.versionHistory.restore")}
                        </div>
                    </div>
                </div>
            ))}
            <RestoreVersionDialog
                isOpen={showRestoreDialog}
                onClose={handleRestoreDialogClose}
                onConfirm={handleRestoreConfirm}
                workflowName={props.selectedRow?.name}
                versionNumber={selectedVersionForRestore?.editorVersion || ""}
                supportsEditorRestore={
                    selectedVersionForRestore && props.selectedRow
                        ? supportsEditorBasedRestore(props.selectedRow.profile, selectedVersionForRestore.editorVersion)
                        : false
                }
                isRestoring={restoring}
                restoreSuccess={restoreSuccess}
                restoreError={restoreError}
            />
        </div>
    );
}

export default SidePanelVersionHistory;
